import React, { Component } from "react";
import { withRouter } from "react-router";
import { withTranslation } from "react-i18next";
import { Transition, Dimmer, Loader } from "semantic-ui-react";

import {
  WelcomeScreen,
  SCREENS,
  AuthenticationScreen,
  CheckInfoScreen,
  OrderScreen,
  ThankYouScreen,
  KioskDisabled,
  SelectLabelScreen,
  StandbyScreen,
  ExternalPlanning,
} from "./screens/index";
import APP_CONFIG from "../../util/app-config";
import Service from "./service";

const STANDBY_TIMEOUT = 60 * 1000;

const initialState = {
  screen: SCREENS.FIRST_SCREEN,
  communication: null,
  extraPhone: null,
  additionalRemark: null,
  parkingNote: null,
  customerAcceptedAgreementsIDs: null,
  customerName: "",
  customerSignature: "",
  isLoading: false,
  standbyMode: false,
  externalUrl: null,
};

class Kiosk extends Component {
  state = {
    ...initialState,
    kiosk: null,
  };

  inactivityTimer = null;

  componentDidMount() {
    this.init();

    window.addEventListener("click", this.startInactivityTimer);
    window.addEventListener("touchstart", this.startInactivityTimer);
  }

  componentWillUnmount() {
    clearTimeout(this.inactivityTimer);

    window.removeEventListener("click", this.resetInactivityTimer);
    window.removeEventListener("touchstart", this.resetInactivityTimer);
  }

  startInactivityTimer = () => {
    if (!this.state.kiosk?.stand_by_image_url) return;

    clearTimeout(this.inactivityTimer);
    this.inactivityTimer = setTimeout(() => {
      this.setState({ standbyMode: true }, () => {
        this.handleGoBackToStartScreen();
      });
    }, STANDBY_TIMEOUT);
  };

  resetInactivityTimer = () => {
    this.setState({ standbyMode: false });
    this.startInactivityTimer();
  };

  init = () => {
    const { match, i18n } = this.props;

    this.setState({ isLoading: true }, () => {
      Service.init({ private_key: match.params.kioskKey, frontend_version: APP_CONFIG.FRONTEND_VERSION })
        .then(res => {
          const kiosk = res?.data?.data ? res.data.data : {};
          i18n.changeLanguage(kiosk.language_code || "en-GB");
          this.setState({ kiosk, isLoading: false }, () => {
            this.startInactivityTimer();
          });
        })
        .catch(err => {
          console.error("Error getting Kiosk", err);

          setTimeout(() => {
            this.init();
          }, 60000);

          this.setState({ isLoading: false });
        });
    });
  };

  handleStartOver = (stateUpdate = {}) => {
    if (stateUpdate.kiosk) {
      this.handleResetState(stateUpdate);
      return;
    }

    this.setState({ isLoading: true }, () => {
      Service.getStatus({ private_key: this.props.match.params.kioskKey, frontend_version: APP_CONFIG.FRONTEND_VERSION })
        .then(response => {
          const { active, confirmation_title, confirmation_image_url } = response?.data?.data ? response.data.data : {};
          this.handleResetState({ kiosk: { ...this.state.kiosk, active, confirmation_title, confirmation_image_url } });
        })
        .catch(() => this.handleResetState(stateUpdate));
    });
  };

  handleStatusUpdate = () => {
    Service.getStatus({ private_key: this.props.match.params.kioskKey })
      .then(response => {
        const { active, confirmation_title, confirmation_image_url } = response?.data?.data ? response.data.data : {};
        this.setState(prevState => ({ kiosk: { ...prevState.kiosk, active, confirmation_title, confirmation_image_url } }));
      })
      .catch(() => console.error("Failed to get status"));
  };

  handleGoBackToStartScreen = () => this.setState({ screen: SCREENS.FIRST_SCREEN });

  handleGoToNextScreen = () => this.setState({ screen: this.state.screen + 1 });

  handleGoToNextScreenWithState = state => this.setState({ ...state, screen: this.state.screen + 1 });

  handleGoToOrderScreenWithState = state => this.setState({ ...state, screen: SCREENS.ORDER });

  handleGoToPreviousScreen = () => this.setState({ screen: this.state.screen - 1 });

  handleNavigateToExternalPage = externalUrl => this.setState({ externalUrl, screen: SCREENS.EXTERNAL_PLANNING });

  handleReturnToKiosk = () => this.setState({ externalUrl: null, screen: SCREENS.WELCOME });

  handleResetState = (stateUpdate = {}) => {
    this.setState({ ...initialState, kiosk: { ...this.state.kiosk, ...stateUpdate.kiosk } });
  };

  exitStandbyMode = () => {
    this.setState({ standbyMode: false });
    this.startInactivityTimer();
  };

  render() {
    const {
      screen,
      communication,
      kiosk,
      extraPhone,
      additionalRemark,
      parkingNote,
      customerAcceptedAgreementsIDs,
      customerName,
      customerSignature,
      isLoading,
      standbyMode,
      externalUrl,
    } = this.state;
    const { softmode } = this.props;

    if (isLoading) {
      return (
        <Transition visible={true} animation="fade" duration={300}>
          <Dimmer active={true}>
            <Loader size="massive" />
          </Dimmer>
        </Transition>
      );
    }

    if (!kiosk) return null;

    if (!kiosk.active) return <KioskDisabled />;

    if (standbyMode && kiosk.stand_by_image_url) return <StandbyScreen standbyImage={kiosk.stand_by_image_url} onTouch={this.exitStandbyMode} />;

    switch (screen) {
      default:
      case SCREENS.WELCOME:
        return (
          <WelcomeScreen
            kiosk={kiosk}
            onStatusUpdate={this.handleStatusUpdate}
            onGoToNextScreen={this.handleGoToNextScreen}
            onNavigateToExternalPage={this.handleNavigateToExternalPage}
            softmode={softmode}
          />
        );

      case SCREENS.AUTH_OPTIONS:
        return (
          <AuthenticationScreen
            onGoToPreviousScreen={this.handleGoToPreviousScreen}
            onGoToNextScreenWithState={this.handleGoToNextScreenWithState}
            onGoToOrderScreenWithState={this.handleGoToOrderScreenWithState}
            onGoBackToStartScreen={this.handleGoBackToStartScreen}
            onAbort={this.handleStartOver}
            onStartOver={this.handleStartOver}
            kiosk={kiosk}
          />
        );

      case SCREENS.CHECK_INFO:
        return <CheckInfoScreen onGoToNextScreenWithState={this.handleGoToNextScreenWithState} communication={communication} onAbort={this.handleStartOver} />;

      case SCREENS.ORDER:
        return <OrderScreen onGoToNextScreenWithState={this.handleGoToNextScreenWithState} communication={communication} kiosk={kiosk} onAbort={this.handleStartOver} />;

      case SCREENS.SELECT_LABEL:
        return (
          <SelectLabelScreen
            kiosk={kiosk}
            communication={communication}
            additionalRemark={additionalRemark}
            parkingNote={parkingNote}
            agreements={customerAcceptedAgreementsIDs}
            extraPhone={extraPhone}
            customerName={customerName}
            customerSignature={customerSignature}
            onGoToNextScreen={this.handleGoToNextScreen}
            onAbort={this.handleStartOver}
          />
        );

      case SCREENS.EXTERNAL_PLANNING:
        return <ExternalPlanning externalUrl={externalUrl} onReturnToKiosk={this.handleReturnToKiosk} />;

      case SCREENS.THANK_YOU:
        return <ThankYouScreen onStartOver={this.handleStartOver} kiosk={kiosk} />;
    }
  }
}

export default withRouter(withTranslation()(Kiosk));
